import { Inject, Injectable } from '@angular/core';
import { CryptoService } from '@Core/services/proprietary/crypto.service';
import { SESSION_STORAGE, WINDOW } from './browser-storage.tokens';

@Injectable({ providedIn: 'root' })
export class SessionStorage implements Storage {
    private readonly STORAGE_KEY_DELIMITER = '|~|';
    private _encryptionMode: boolean = true;

    constructor(
        private cryptoService: CryptoService,
        @Inject(SESSION_STORAGE) readonly _storage: Storage,
        @Inject(WINDOW) private readonly windowRef: Window
    ) { }

    public setEncryptionMode(mode: boolean): void {
        this._encryptionMode = mode;
        this.checkEncryptionMode();
    }

    public getEncryptionMode(): boolean {
        return this._encryptionMode;
    }

    // Asynchronous method that will encrypt the value and part of the key upon saving to session storage if the Encryption Mode
    // returned from the ContentServer is set to 'Y', otherwise the data will be stored in plain text
    public async setItemAsync(key: string, value: any): Promise<boolean> {
        return new Promise(async (resolve, reject) => {
            try {
                if (key && key !== null && key !== undefined) {
                    if (this.isG2NonEncryptedEntry(key)) {
                        this._storage.setItem(key, value);
                        resolve(true);
                    } else {
                        const hashedKey = await this.createKey(key);
                        const valueToStore = this._encryptionMode ? await this.cryptoService.encrypt(value) : value;
                        this._storage.setItem(hashedKey, valueToStore);
                        resolve(true);
                    }
                } else {
                    resolve(false);
                }
            } catch (error) {
                console.log(`SetItem - in catch block with error of: ${error}`);
                reject(error);
            }
        });
    }

    // Asynchronous method that will return plain text values from session storage if provided a valid key
    public async getItemAsync(key: string): Promise<any | null> {
        return new Promise<any | null>(async (resolve) => {
            let valueToReturn;
            let storedValue;

            if (!key || key == null || key == undefined) {
                resolve(null);
            }

            if (this.isG2NonEncryptedEntry(key)) {
                valueToReturn = this._storage.getItem(key);
                if (valueToReturn) {
                    resolve(valueToReturn);
                }
            } else {
                const hashedKey = await this.createKey(key);
                if (hashedKey) {
                    storedValue = this._storage.getItem(hashedKey);
                    if (storedValue) {
                        valueToReturn = this._encryptionMode ? await this.cryptoService.decrypt(storedValue) : storedValue;
                        if (valueToReturn === null) {
                            this._storage.removeItem(hashedKey);
                        }
                        resolve(valueToReturn);
                    } else {
                        resolve(null);
                    }
                } else {
                    resolve(null);
                }
            }
        });
    }

    // Asynchronous method that will remove an entry from session storage if provided a valid key
    public async removeItemAsync(key: string): Promise<boolean> {
        return new Promise(async (resolve) => {
            if (!key || key == null || key == undefined) {
                resolve(false);
            }

            const storedKeys = Object.keys(this._storage);

            if (this.isG2NonEncryptedEntry(key) || !this.isG2RelatedItem(key)) {
                if (storedKeys.indexOf(key) !== -1) {
                    this._storage.removeItem(key);
                    resolve(true);
                }
            } else {
                const hashedKey = await this.createKey(key);
                if (hashedKey) {
                    this._storage.removeItem(hashedKey);
                    resolve(true);
                } else {
                    resolve(false);
                }
            }
        });
    }

    public async clearAsync(): Promise<boolean> {
        return new Promise((resolve) => {
            this._storage.clear();
            resolve(true);
        });
    }

    get length(): number {
        return this._storage.length;
    }

    key(index: number): string | null {
        return this._storage.key(index);
    }

    public async removeItemsOfTypeAsync(type: string) {
        const keysToRemove = Object.keys(this._storage).filter((key) => key.indexOf(type) > -1);
        for (const key of keysToRemove) {
            this._storage.removeItem(key);
        }
    }

    // Synchronous method needed to support multi-tab functionality
    public getItem(key: string) {
        if (key && key !== null && key !== undefined) {
            const prefix = this._encryptionMode ? 'g2e' : 'g2d';
            let finalKey;
            if (this.isG2RelatedItem(key) && !this.isG2NonEncryptedEntry(key) && !key.startsWith('g2')) {
                finalKey = `${prefix}.${key}`;
            } else {
                finalKey = `${key}`;
            }
            return this._storage.getItem(finalKey);
        }
    }

    // Synchronous method that DOES NOT support encryption of the key or value needed to support multi-tab functionality
    public setItem(key: string, value: any) {
        if (key && key !== null && key !== undefined) {
            const prefix = this._encryptionMode ? 'g2e' : 'g2d';
            let finalKey;
            if (this.isG2RelatedItem(key) && !this.isG2NonEncryptedEntry(key) && !key.startsWith('g2')) {
                finalKey = `${prefix}.${key}`;
            } else {
                finalKey = `${key}`;
            }
            this._storage.setItem(finalKey, value);
        }
    }

    // Synchronous method needed to support multi-tab functionality
    public removeItem(key: string) {
        if (key && key !== null && key !== undefined) {
            const prefix = this._encryptionMode ? 'g2e' : 'g2d';
            let finalKey;
            if (this.isG2RelatedItem(key) && !this.isG2NonEncryptedEntry(key) && !key.startsWith('g2')) {
                finalKey = `${prefix}.${key}`;
            } else {
                finalKey = `${key}`;
            }

            const storageKeys = Object.keys(this._storage);

            if (storageKeys.indexOf(finalKey) !== -1) {
                this._storage.removeItem(finalKey);
            }
        }
    }

    public clear() {
        this._storage.clear();
    }

    public removeItemOfType(type: string) {
        const keysToRemove = Object.keys(this._storage).filter((key) => key.indexOf(type) > -1);
        for (const key of keysToRemove) {
            this._storage.removeItem(key);
        }
    }

    public getAllKeys() {
        return Object.keys(this._storage);
    }

    public getAllG2Keys(): string[] {
        let g2Keys: string[] = [];
        const storageKeys = Object.keys(this._storage);
        if (storageKeys.length > 0) {
            g2Keys = storageKeys
                .filter((key) => this.isG2RelatedItem(key))
                .map((key) => (key.startsWith('g2') ? key.slice(4) : key));
        }
        return g2Keys;
    }

    private async createKey(k: string) {
        if (!k || k === null || k === undefined) {
            return;
        }

        let key = k.toLocaleLowerCase();

        if (!this.isG2RelatedItem(key)) {
            return;
        }

        if (this.isG2NonEncryptedEntry(key)) {
            return key;
        }

        if (key.startsWith('g2')) {
            key = key.slice(4);
        }

        const prefix = this._encryptionMode ? 'g2e' : 'g2d';
        let finalKey;

        if (this._encryptionMode == false) {
            finalKey = `${prefix}.${key}`;
        } else {
            if (key.startsWith('datacache') && key.indexOf(';') > -1) {
                let firstSemiColonIndex = key.indexOf(';');
                let tranType = key.slice(0, firstSemiColonIndex);
                let tranDetail = firstSemiColonIndex > -1 ? key.slice(firstSemiColonIndex) : '';
                let hashedSection = await this.cryptoService.hashKey(tranDetail);
                finalKey = `${prefix}.${tranType};${this.STORAGE_KEY_DELIMITER}${hashedSection}`;
            } else {
                finalKey = `${prefix}.${key}`;
            }
        }

        return finalKey;
    }

    private checkEncryptionMode() {
        let allKeys: string[] = [];
        allKeys = Object.keys(this._storage);
        let itemsToRemove: string[] = [];

        if (allKeys.length > 0) {
            if (this._encryptionMode) {
                itemsToRemove = allKeys.filter((key) => this.isG2RelatedItem(key)).filter((key) => !key.startsWith('g2e'));
            } else {
                itemsToRemove = allKeys.filter((key) => this.isG2RelatedItem(key)).filter((key) => !key.startsWith('g2d'));
            }

            if (itemsToRemove.length > 0) {
                for (const key of itemsToRemove) {
                    if (!this.isG2NonEncryptedEntry(key)) {
                        this._storage.removeItem(key);
                    }
                }
            }
        }
    }

    private isG2RelatedItem(key: string) {
        if (key && key !== null && key !== undefined) {
            if (key && (key.startsWith('persist:viewer') || key.startsWith('toolData-'))) {
                return false;
            }
            return true;
        }
        return false;
    }

    private isG2NonEncryptedEntry(key: string) {
        if (key && key !== null && key !== undefined) {
            let k = key.toLocaleLowerCase();
            if (
                k.startsWith('suremgatab') ||
                k.endsWith('configupdatetimestamp') ||
                k.startsWith('currentuser') ||
                k.startsWith('preference:user') ||
                k.startsWith('sessiontab') ||
                k.startsWith('tab:') ||
                k.startsWith('username')
            ) {
                return true;
            }
            return false;
        }
        return false;
    }
}
